import { useMemo, useState } from 'react';

import { IconShieldCheck, IconZoomOutline } from '@iwoca/icons';
import { Accordion, Input } from '@iwoca/orion';
import { clsx } from 'clsx';

import styles from './BankSelectionStep.module.css';
import { useGetContext } from '../../../../../api/lendingApi/lendingApiQueries';
import { TOnlineBank } from '../../../OpenBanking/OpenBanking.types';

const POPULAR_BANK_IDS = [
  'ob-monzo',
  'ob-natwest',
  'ob-lloyds',
  'ob-barclays',
  'ob-revolut',
  'ob-starling',
  'ob-tide',
];

type TBankConnectionStepProps = {
  onBankSelect: (bank: TOnlineBank) => void;
};

const BankConnectionStep = ({ onBankSelect }: TBankConnectionStepProps) => {
  const { context } = useGetContext();

  const [searchTerm, setSearchTerm] = useState('');

  const getUKBankList = (): TOnlineBank[] => {
    if (!context) return [];

    const truelayerBanks: TOnlineBank[] = (
      context.open_banking.truelayer_banks ?? []
    ).map((bank) => ({ ...bank, provider: 'truelayer' as const }));

    return [...truelayerBanks];
  };

  const bankList = getUKBankList();

  const popularBanks = useMemo(
    () =>
      POPULAR_BANK_IDS.map((id) =>
        bankList.find((bank) => bank.bank_id === id),
      ).filter(Boolean) as TOnlineBank[],
    [bankList],
  );

  const filteredBanks =
    searchTerm === ''
      ? popularBanks
      : bankList.filter((bank) =>
          bank.bank_name.toLowerCase().includes(searchTerm.toLowerCase()),
        );

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const showPopularBanksLabel = searchTerm === '' && popularBanks.length > 0;

  return (
    <div className={styles.container}>
      <Accordion
        icon={<IconShieldCheck className={styles.shieldIcon} />}
        title={
          <span className={styles.headerText}>
            Fastest and most secure way to get a decision.{' '}
            <span className={styles.howItWorksLink}>How does it work?</span>
          </span>
        }
        variant="primary"
        frame={false}
        className={styles.accordion}
        headerClassName={styles.accordionHeader}
        titleClassName={styles.accordionTitle}
      >
        <p className={styles.accordionContent}>
          Securely share your bank account’s data. Simply choose your bank below
          and then log in through your bank’s portal when prompted.
        </p>
      </Accordion>

      <Input
        iconLeft={<IconZoomOutline />}
        className={styles.searchBar}
        placeholder="Search for your bank..."
        onChange={handleSearch}
        value={searchTerm}
      />

      {showPopularBanksLabel && (
        <p className={styles.popularBanksLabel}>Most popular banks</p>
      )}

      <div
        className={clsx(styles.banksGrid, {
          [styles.banksGridError]: filteredBanks.length === 0,
        })}
      >
        {filteredBanks.map((bank) => (
          <button
            key={bank.bank_id}
            className={styles.bankButton}
            onClick={() => onBankSelect(bank)}
          >
            <figure className={styles.bankFigure}>
              <img
                src={bank.logo_url}
                alt={bank.bank_name}
                className={styles.bankLogo}
              />
              <figcaption className={styles.bankName}>
                {bank.bank_name}
              </figcaption>
            </figure>
          </button>
        ))}
        {filteredBanks.length === 0 && (
          <p className={styles.noBanksError}>
            Unfortunately the bank you have chosen is not supported.
          </p>
        )}
      </div>
    </div>
  );
};

export default BankConnectionStep;
